import React, { useState, useEffect, useCallback } from "react";
import {
  Rate,
  Input,
  Form,
  Radio,
  Spin,
  Button,
  Layout,
  Popconfirm,
  Progress,
  Steps,
} from "antd";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import { useLocation, useNavigate } from "react-router-dom";
import {
  MdChevronRight,
  MdChevronLeft,
  MdThumbDown,
  MdThumbUp,
} from "react-icons/md";
import { upperFirst } from "lodash";
import { useAppDispatch, useAppSelector } from "../../hooks/reduxHooks";
import {
  getProductListApi,
  submitProductReviewApi,
} from "../../services/productOpinion.service";
import useGetUserDetails from "../../hooks/useGetUserDetails";
import AppButton from "../../components/common/buttons/button";
import { getProfileApi } from "../../services/profile.service";
import PageLoader from "../../components/common/loader/pageLoader";
import Header from "../../layout/protectedLayout/protectedLayoutStore/headers/pageHeader";
import Payment from "./../../containers/payment";
import PaymentFooter from "../../components/payment/sections/paymentFooter";
import { createPaymentIntentApi } from "../../services/payment.service";
import HappySvg from "../../assets/images/gift.svg";
import Trophy from "../../assets/images/trophy.svg";
import { CheckOutlined } from "@ant-design/icons";
import { debounce } from "lodash";
import ReactGA from "react-ga4";
import { storeUserData } from "../../store/slice/profile.slice";

interface ProductOpinionInterface {}

const MIN_CHAR_COUNT = 80;

const ProductOpinion: React.FC<ProductOpinionInterface> = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [activeProduct, setActiveProduct] = useState(0);
  const params = useLocation().search;
  const id = new URLSearchParams(params).get("id");
  const [products, setProducts] = useState<any>([]);
  const [totalEarned, setTotalEarned] = useState(0);
  const userDetails: any = useGetUserDetails();
  const dispatch = useAppDispatch();
  const [swiper, setSwiper] = useState<any>(null);
  const [clientSecret, setClientSecret] = useState<any>("");
  const [pricing, setPricing] = useState<any>();
  const [initialPricing, setInitialPricing] = useState<any>();
  const [loadingPayment, setLoadingPayment] = useState(false);
  const [surveyId, setSurveyId] = useState<any>();
  const [data, setData] = useState<any>();
  const [isZeroAmount, setIsZeroAmount] = useState(false);
  const navigate = useNavigate();
  const [isLuckyDrawWinner, setIsLuckyDrawWinnner] = useState(false);
  const [inputValue, setInputValue] = useState<any>();
  const [inputTextArea, setInputTextArea] = useState<any>();
  const [currentStep, setCurrentStep] = useState(2);

  const state = useAppSelector((state) => state);
  const { userData }: { userData?: any } = state.profile;

  const getCurrentStep: any = () => {
    let currentStep = 0;

    // if (userData?.lucky_draw_status === "claimed") {
    //   return (currentStep = 2); // Return null in this case
    // }
    // console.log("userData?.survey_status", userData?.survey_status);

    switch (userData?.survey_status) {
      case "draft":
        currentStep = 0;
        break;

      case "paid":
        currentStep = 2;
        break;

      case "answered":
        if (userData?.lucky_draw_status === "claimed") {
          currentStep = 2;
        } else {
          currentStep = 1;
        }
        break;

      case "responded":
        currentStep = 3;
        break;

      default:
        currentStep = 1;
    }

    currentStep = currentStep < 2 ? 2 : currentStep;

    return currentStep;
  };

  const getContinueButton = (upcomingStatus?: string) => {
    switch (userData?.survey_status) {
      case "draft":
        return (
          <>
            <Button
              size="small"
              type="primary"
              className="processing-btn stepper-btn"
              onClick={() => {
                ReactGA.event({
                  category: "Store",
                  action: `Revise Skincare Timeline CTA Button Click`,
                  label: "Go to Payment",
                  nonInteraction: true, // optional, true/false
                  transport: "xhr",
                });
                navigate(`/product-opinion?id=${userData?.active_survey_id}`);
              }}
            >
              <span>Continue</span>
            </Button>
          </>
        );

      case "paid":
        return (
          <>
            <Button
              size="small"
              type="primary"
              className="stepper-btn processing-btn"
              onClick={() => {
                ReactGA.event({
                  category: "Store",
                  action: `Revise Skincare Timeline CTA Button Click`,
                  label: "Go to quiz",
                  nonInteraction: true, // optional, true/false
                  transport: "xhr",
                });
                navigate("/store");
              }}
            >
              <span>Continue</span>
            </Button>
          </>
        );
      case "answered":
        if (userData?.lucky_draw_status === "claimed") {
          return <></>;
        }
        return (
          <>
            <Button
              size="small"
              type="primary"
              className="stepper-btn processing-btn"
              onClick={() => {
                ReactGA.event({
                  category: "Store",
                  action: `Revise Skincare Timeline CTA Button Click`,
                  label: "Go to quiz",
                  nonInteraction: true, // optional, true/false
                  transport: "xhr",
                });
                navigate(`/product-opinion?id=${userData?.active_survey_id}`);
              }}
            >
              <span>Proceed to Pay</span>
            </Button>
          </>
        );

      default:
        return <></>;
    }
  };

  useEffect(() => {
    if (!userDetails?.userDetails) {
      fetchProfile();
    } else {
      setIsLuckyDrawWinnner(
        userDetails?.userDetails?.lucky_draw_status === "claimed"
      );
    }
  }, []);

  useEffect(() => {
    fetchProductList();
  }, []);

  const shuffleArray = (array: any[]) => {
    for (let i = array.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [array[i], array[j]] = [array[j], array[i]];
    }
    return array;
  };

  const createPaymentIntent = (survey_id: number) => {
    // setLoading(true);
    dispatch(
      createPaymentIntentApi({
        data: { survey_id: survey_id },
        finalCallback: () => {
          setLoading(false);
        },
        successCallback: (data?: any) => {
          setPricing(data?.amount);
          setInitialPricing(data?.initial_amount);
          setClientSecret(data?.client_secret);
          setSurveyId(data?.survey_id);
          // setDiscountType(data?.discount_type);
          // setDiscountAmt(data?.discount_amount);
          setData(data);
          if (data?.client_secret === null) {
            setLoadingPayment(false);
            setIsZeroAmount(true);
          }
        },
      })
    );
  };

  const userFirstName = userDetails?.userDetails?.name?.split(" ")[0];

  const message =
    "Are you sure you want to proceed to another product? If you move to another product without submitting, your filled out data will be lost.";

  const SwiperNextButton = () => {
    const confirm = (
      e: React.MouseEvent<HTMLElement, MouseEvent> | undefined
    ) => {
      form.resetFields();
      swiper?.slideNext();
      setInputValue("");
      setInputTextArea("");
    };

    const cancel = (
      e: React.MouseEvent<HTMLElement, MouseEvent> | undefined
    ) => {
      console.log(e);
    };

    const currentProduct = products[activeProduct]?.reviewSubmitted;

    return (
      <div>
        {activeProduct + 1 >= products.length ? (
          <div
            className="empty-next-btn"
            style={{
              opacity: 0.5,
            }}
          >
            <MdChevronRight
              size={22}
              style={{
                color: "#fff",
              }}
            />
          </div>
        ) : (inputValue?.length || inputTextArea?.length) && !currentProduct ? (
          <Popconfirm
            title={message}
            onConfirm={confirm}
            onCancel={cancel}
            okText="Yes"
            cancelText="No"
          >
            <Button
              type="ghost"
              shape="circle"
              className="nav-btn"
              size="small"
            >
              <MdChevronRight size={22} color="#fff" />
            </Button>
          </Popconfirm>
        ) : (
          <Button
            type="ghost"
            shape="circle"
            className="nav-next-btn nav-btn-next"
            size="small"
            onClick={() => {
              swiper?.slideNext();
            }}
          >
            <MdChevronRight size={22} color="#fff" />
          </Button>
        )}
      </div>
    );
  };

  const SwiperPrevButton = () => {
    const confirm = (
      e: React.MouseEvent<HTMLElement, MouseEvent> | undefined
    ) => {
      form.resetFields();
      swiper?.slidePrev();
      setInputValue("");
      setInputTextArea("");
    };

    const cancel = (
      e: React.MouseEvent<HTMLElement, MouseEvent> | undefined
    ) => {
      console.log(e);
    };

    const currentProduct = products[activeProduct]?.reviewSubmitted;

    return (
      <div>
        {activeProduct <= 0 ? (
          <div
            className="empty-prev-btn"
            style={{
              opacity: 0.5,
            }}
          >
            <MdChevronLeft
              size={22}
              style={{
                color: "#fff",
              }}
            />
          </div>
        ) : (inputValue?.length || inputTextArea?.length) && !currentProduct ? (
          <Popconfirm
            title={message}
            onConfirm={confirm}
            onCancel={cancel}
            okText="Yes"
            cancelText="No"
          >
            <Button
              type="ghost"
              shape="circle"
              className="nav-btn"
              size="small"
            >
              <MdChevronLeft size={22} color="#fff" />
            </Button>
          </Popconfirm>
        ) : (
          <Button
            type="ghost"
            shape="circle"
            className="nav-prev-btn"
            size="small"
            onClick={() => {
              swiper?.slidePrev();
            }}
          >
            <MdChevronLeft size={22} color="#fff" />
          </Button>
        )}
      </div>
    );
  };

  const fetchProfile = () => {
    setLoading(true);
    dispatch(
      getProfileApi({
        finalCallback: () => {
          setLoading(false);
        },
        successCallback: (res: any) => {
          setIsLuckyDrawWinnner(res?.lucky_draw_status === "claimed");
        },
        unauthenticatedCallback: () => {},
      })
    );
  };

  const handleSwiperUpdate = () => {
    if (swiper) {
      swiper.update();
      const firstUnreviewedProductIndex = products.findIndex(
        (product: any) => !product.reviewSubmitted
      );
      swiper.slideTo(
        firstUnreviewedProductIndex >= 0 ? firstUnreviewedProductIndex : 0
      );
    }
  };

  const fetchProductList = () => {
    setLoading(true);
    getProductListApi({
      id: id,
      finalCallback: () => {
        setLoading(false);
      },
      successCallback: (res: any) => {
        const allProducts = res?.data?.products || [];
        const reviewedProducts = allProducts.filter(
          (product: any) => product.reviewSubmitted
        );
        const unreviewedProducts = allProducts.filter(
          (product: any) => !product.reviewSubmitted
        );
        const shuffledUnreviewedProducts = shuffleArray(unreviewedProducts);
        const combinedProducts = [
          ...reviewedProducts, // Prioritize reviewed products
          ...shuffledUnreviewedProducts, // Shuffle unreviewed products
        ];

        const firstUnreviewedProductIndex = allProducts.findIndex(
          (product: any) => !product.reviewSubmitted
        );
        const initialProduct =
          combinedProducts[firstUnreviewedProductIndex] || combinedProducts[0];

        setProducts(combinedProducts);
        setTotalEarned(res?.data?.total_earned);
        form.setFieldsValue({
          ...(initialProduct ?? {}),
        });

        handleSwiperUpdate();
      },
    });
  };

  const handleFinish = (values: any) => {
    let subObj = {
      ...values,
      product_id: products[activeProduct]?.id,
      survey_id: id,
    };
    setSubmitting(true);
    submitProductReviewApi({
      data: subObj,
      finalCallback: () => {
        setSubmitting(false);
      },
      successCallback: (res: any) => {
        // Prioritize reviewed products and shuffle unreviewed ones
        const allProducts = res?.data?.products || [];
        const reviewedProducts = allProducts.filter(
          (product: any) => product.reviewSubmitted
        );
        const unreviewedProducts = allProducts.filter(
          (product: any) => !product.reviewSubmitted
        );
        const shuffledUnreviewedProducts = shuffleArray(unreviewedProducts);
        const combinedProducts = [
          ...reviewedProducts, // Prioritize reviewed products
          ...shuffledUnreviewedProducts, // Shuffle unreviewed products
        ];
        setCurrentStep(3);

        setProducts(combinedProducts);
        setTotalEarned(res?.data?.total_earned || 0);
        // createPaymentIntent(userDetails?.userDetails?.active_survey_id);
        if (swiper && activeProduct + 1 < combinedProducts.length) {
          swiper.slideNext();
        }
        setInputValue("");
        setInputTextArea("");
      },
    });
  };

  const handleLogout = () => {
    dispatch(storeUserData({}));
    localStorage.clear();
    window.location.replace("https://missingskincare.com");
    setLoading(true);
  };

  // const isLuckyDrawWinner = true;
  // userDetails?.userDetails?.lucky_draw_status === "claimed" ||
  // userDetails?.userDetails?.lucky_draw_status === "winner";

  const handleInputChange = useCallback(
    debounce((e) => {
      let val = e.target.value;
      setInputValue(val);
    }, 300),
    []
  );

  const handleDislikeInputChange = useCallback(
    debounce((e) => {
      let val = e.target.value;
      setInputTextArea(val);
    }, 300),
    []
  );

  const filterSubmittedReviewOnly = products.filter(
    (review: any) => review?.reviewSubmitted
  ).length;

  let percentage = filterSubmittedReviewOnly
    ? Math.round((totalEarned / 70) * 100)
    : 0; // Calculate the percentage based on a maximum of $70
  if (totalEarned >= 70) {
    percentage = 95; // Ensure the percentage doesn't exceed 100%
  }

  const getExtraText = () => {
    const submittedReviews = filterSubmittedReviewOnly;
    const totalProducts = products.length;

    if (submittedReviews >= 5 && submittedReviews === totalProducts) {
      return "You're the Best!";
    } else if (submittedReviews >= 5) {
      return "Keep Going to Find Better Products.";
    } else if (submittedReviews > 0) {
      return `${percentage}%`;
    } else {
      return "Review Below to Earn More";
    }
  };

  return loading ? (
    <PageLoader />
  ) : (
    <Layout>
      {/* <Header hideSideNav={true} /> */}
      <div className="product-opinion">
        {/* {!isLuckyDrawWinner && (
          <div className="product-progress-bar">
            <div className="reward-holder">
              <h3 className="reward-text">
                Reward <br /> Earned
              </h3>
              <span className="total-earned-text">
                ${totalEarned.toFixed(2)}
              </span>
            </div>
            <div className="progress-holder">
              <Progress
                strokeLinecap="butt"
                percent={percentage}
                format={() => ""}
                strokeColor="#d7e57a"
                strokeWidth={8}
              />
              <div className="hero-text">
                <span className="review-percentage">{getExtraText()}</span>
              </div>
            </div>
            <div className="trophy-holder">
              <img src={Trophy} alt="trophy" width={40} />
            </div>
          </div>
        )} */}
        <div className="waiting-page-wrapper">
          <div className="waiting-page waiting-page--opinion">
            <h1>Thank you {userData?.user_information?.first_name}!</h1>
            <div className="content w-100">
              <div className="progress">
                <Steps
                  className="progress__steps"
                  size="small"
                  current={currentStep}
                  labelPlacement="vertical"
                  responsive
                  items={[
                    {
                      title: "Skin Quiz Answered",
                    },
                    {
                      title: "Personalizing Your Skincare",
                    },
                    {
                      title: `Payment ${
                        userData?.lucky_draw_status === "claimed"
                          ? "Waived"
                          : "Received"
                      }`,
                      // userData?.lucky_draw_status === "claimed"
                      //   ? "(Lucky Draw Winner)"
                      //   : userData?.survey_status === "answered"
                      //   ? getContinueButton("toAnswer")
                      //   : null,
                    },
                    {
                      title: "Get Email in Two Business Days or Less",
                    },
                  ]}
                />
              </div>
              {/* <h3>
            We will handcraft your personal skincare space and email you in 2
            business days or less.{" "}
          </h3> */}
            </div>
          </div>
        </div>
        <div className="product-opinion__body">
          <div className="product-opinion__body__header">
            <div className="left-section">
              {userFirstName ? (
                <div className="content-holder">
                  <h2 className="thanks">
                    {upperFirst(userFirstName)}, your opinion matters!
                  </h2>
                  <p className="description">
                    Sharing your product opinion truly helps to find and build
                    your best skincare routine.
                  </p>
                </div>
              ) : (
                <Spin spinning />
              )}
            </div>
          </div>
          <Form form={form} onFinish={handleFinish}>
            {products?.length ? (
              <div className="products">
                <Swiper
                  onSlideChange={(val: any) => {
                    form.setFieldsValue({
                      ...(products[val?.activeIndex] ?? {}),
                    });
                    setActiveProduct(val?.activeIndex);
                  }}
                  noSwiping={true}
                  allowTouchMove={false}
                  onSwiper={(swiper: any) => setSwiper(swiper)}
                  navigation={false}
                >
                  {products?.map((product: any, productIndex: number) => {
                    return (
                      <SwiperSlide key={productIndex?.toString()}>
                        <Spin spinning={submitting || loading}>
                          <div className="products__item">
                            <div className="products__item__title">
                              <div className="products__item__title__row">
                                <span>
                                  {product?.brand_name ? (
                                    <>{product?.brand_name} - </>
                                  ) : (
                                    <></>
                                  )}
                                  {product?.name}
                                </span>
                                <Form.Item
                                  name="stars"
                                  rules={[
                                    {
                                      required: true,
                                      message: "This field is required",
                                    },
                                  ]}
                                >
                                  <Rate
                                    className="rating"
                                    disabled={product?.reviewSubmitted}
                                  />
                                </Form.Item>
                              </div>
                            </div>
                            <div className="textarea-with-icon">
                              <MdThumbUp
                                className="icon"
                                style={{
                                  color: "#6489b1",
                                }}
                              />
                              <Form.Item name="like_description">
                                <Input.TextArea
                                  placeholder="What did you like about this product?"
                                  readOnly={product?.reviewSubmitted}
                                  onChange={handleInputChange}
                                  // minLength={80}
                                  showCount={{
                                    formatter: ({ count }) => `${count}`,
                                  }}
                                />
                              </Form.Item>
                            </div>
                            <div className="textarea-with-icon">
                              <MdThumbDown
                                className="icon"
                                style={{
                                  color: "#fe595a",
                                }}
                              />
                              <Form.Item name="dislike_description">
                                <Input.TextArea
                                  placeholder="What did you dislike about this product?"
                                  readOnly={product?.reviewSubmitted}
                                  onChange={handleDislikeInputChange}
                                  // minLength={80}
                                  showCount={{
                                    formatter: ({ count }) => `${count}`,
                                  }}
                                />
                              </Form.Item>
                            </div>

                            <div className="products__item__footer">
                              <div className="recommend">
                                Did this product meet your expectation?
                                <Form.Item
                                  name="recommend"
                                  rules={[
                                    {
                                      required: true,
                                      message: "This field is required",
                                    },
                                  ]}
                                >
                                  <Radio.Group
                                    className="recommend__radio"
                                    onChange={(val?: any) => {}}
                                    disabled={product?.reviewSubmitted}
                                    // value={value}
                                  >
                                    <Radio value={1}>Yes</Radio>
                                    <Radio value={0}>No</Radio>
                                  </Radio.Group>
                                </Form.Item>
                              </div>
                              {product?.reviewSubmitted ? (
                                <div
                                  style={{
                                    marginTop: "10px",
                                  }}
                                >
                                  <AppButton
                                    className="btn-primary cta-btn submit-btn"
                                    title="Submitted"
                                    icon={
                                      <CheckOutlined
                                        style={{
                                          fontSize: "24px",
                                        }}
                                      />
                                    }
                                    disabled={product?.reviewSubmitted}
                                    style={{
                                      opacity: 0.6,
                                    }}
                                  />
                                </div>
                              ) : (
                                <div>
                                  <AppButton
                                    title="Submit"
                                    htmlType="submit"
                                    className="btn-primary cta-btn submit-btn"
                                    loading={submitting}
                                    disabled={product?.reviewSubmitted}
                                  />
                                </div>
                              )}
                            </div>
                          </div>
                        </Spin>
                      </SwiperSlide>
                    );
                  })}
                  <div className="product-slider">
                    <SwiperPrevButton />
                    <SwiperNextButton />
                  </div>
                </Swiper>
              </div>
            ) : (
              <>No products available</>
            )}
          </Form>
          <p className="notice-text">
            (*Reviewing products are optional but recommended)
          </p>
          <div className="waiting-page__buttons">
            <Button
              className="btn-primary logout"
              type="dashed"
              onClick={handleLogout}
            >
              Logout
            </Button>
          </div>
        </div>
        {isLuckyDrawWinner ? (
          <>
            <div className="lucky-draw-winner-button">
              <img src={HappySvg} alt="" width={300} />
              <h3>
                Please accept our warmest congratulations on your win! As the
                winner, your payment process has been skipped and you can
                directly go to the next step.
              </h3>
              <AppButton
                title="Proceed"
                htmlType="button"
                className="btn-primary cta-btn"
                onClick={() => {
                  navigate("/processing");
                }}
              />
            </div>
          </>
        ) : (
          <>
            {/* <Payment
              createPaymentIntent={createPaymentIntent}
              clientSecret={clientSecret}
              pricing={pricing}
              initialPricing={initialPricing}
              loadingPayment={loadingPayment}
              surveyId={surveyId}
              data={data}
              isZeroAmount={isZeroAmount}
              setLoadingPayment={setLoadingPayment}
            />
            <div className="payment-footer">
              <PaymentFooter />
            </div> */}
          </>
        )}
      </div>
    </Layout>
  );
};

export default ProductOpinion;
